import { Card, Flex, Tabs, TabsProps } from "antd";
import CardSectionTitle from "components/CardSectionTitle.component";
import { useTabParam } from "hooks/useTabParam";
import { useMemo } from "react";
import { translate } from "shared/languages";
import { BookingDto } from "shared/types";
import { BookingStatus } from "shared/types/enum";
import { BOOKING_STATUS_LABELS } from "shared/utils";
import CustomerBookingTable from "./CustomerBookingTable";

// TODO: when an analytics tab lands, surface `waitlist_expired` counts here
// (unmet demand per customer). Today these rows are intentionally hidden from
// all four buckets below.
export const CustomerBooking = ({
  bookings,
  isLoading,
}: {
  bookings: BookingDto[];
  isLoading: boolean;
}) => {
  // Own param name: this sits inside the customer detail's ?tab=3 pane, so
  // the sibling-param copy in useTabParam keeps both in the URL together.
  const { activeKey, onChange } = useTabParam("confirmed", "bookingStatus", [
    "confirmed",
    "waitlist",
    "completed",
    "refund",
  ]);

  const grouped = useMemo(() => {
    const all = bookings ?? [];
    return {
      // Active = awaiting confirmation (Pending) or confirmed.
      confirmed: all.filter(
        (b) =>
          b.booking_status === BookingStatus.Confirmed ||
          b.booking_status === BookingStatus.Pending,
      ),
      waitlist: all.filter(
        (b) => b.booking_status === BookingStatus.Waitlist,
      ),
      // Selesai = the session happened: attended (Completed/Refunded) or
      // consumed by a no-show. Refunded sits here because the client came;
      // its badge carries the credit-returned nuance.
      completed: all.filter(
        (b) =>
          b.booking_status === BookingStatus.Completed ||
          b.booking_status === BookingStatus.NoShow ||
          b.booking_status === BookingStatus.Refunded,
      ),
      // Batal = the pre-class cancel only (the legacy "refund" DB value).
      refund: all.filter(
        (b) => b.booking_status === BookingStatus.Cancelled,
      ),
    };
  }, [bookings]);

  const items: TabsProps["items"] = [
    {
      key: "confirmed",
      // Tab names are booking-status groups: fixed English like the badges
      // they sit above (Confirmed also holds Pending; "Completed & No Show"
      // also holds Refunded, as before).
      label: `${BOOKING_STATUS_LABELS[BookingStatus.Confirmed]} (${grouped.confirmed.length})`,
      children: (
        <CustomerBookingTable
          bookings={grouped.confirmed}
          isLoading={isLoading}
          showSearchStaff={true}
        />
      ),
    },
    {
      key: "waitlist",
      label: `${BOOKING_STATUS_LABELS[BookingStatus.Waitlist]} (${grouped.waitlist.length})`,
      children: (
        <CustomerBookingTable
          bookings={grouped.waitlist}
          isLoading={isLoading}
          showSearchStaff={true}
        />
      ),
    },
    {
      key: "completed",
      label: `${BOOKING_STATUS_LABELS[BookingStatus.Completed]} & ${BOOKING_STATUS_LABELS[BookingStatus.NoShow]} (${grouped.completed.length})`,
      children: (
        <CustomerBookingTable
          bookings={grouped.completed}
          isLoading={isLoading}
          showSearchStaff={true}
          sortAscending={false}
        />
      ),
    },
    {
      key: "refund",
      label: `${BOOKING_STATUS_LABELS[BookingStatus.Cancelled]} (${grouped.refund.length})`,
      children: (
        <CustomerBookingTable
          bookings={grouped.refund}
          isLoading={isLoading}
          showSearchStaff={true}
          sortAscending={false}
        />
      ),
    },
  ];

  return (
    <Card>
      <Flex vertical gap={18}>
        <CardSectionTitle title={translate("title.schedule_bookings")} />
        <Tabs
          size="small"
          activeKey={activeKey}
          onChange={onChange}
          items={items}
        />
      </Flex>
    </Card>
  );
};
